/**
 * AstorWalkBlock — Astor walk-through panel beneath the Sign Queue tab list
 *
 * Mint-bordered panel that offers a guided plain-English walkthrough of
 * the highest-urgency pending document before the borrower signs.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Astor surface 1)
 * - The spec calls for `AstorBriefingCard` from design-system-ui; that
 *   component is in the design pipeline but not yet exported, so we
 *   hand-roll the equivalent surface here using the same primitives the
 *   Briefing card will use (mint border, mint CTA, avatar dot).
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() for tints, baseColors.teal for mint (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on the CTA (portals.md)
 *
 * TODO(#design-system-pipeline): replace with `<AstorBriefingCard>` once
 *   that component lands in @ops-matrix/design-system-ui.
 */

import EastIcon from '@mui/icons-material/East';
import { alpha, Box, baseColors, GlassButton, Stack, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { AstorAvatar } from '../loans/AstorAvatar';

export interface AstorWalkBlockProps {
  /** Optional document title to inject into the body copy (e.g. "the Note"). */
  documentLabel?: string;
  /** Click handler — parent opens the walk-through experience. */
  onWalkMeThrough: () => void;
}

export function AstorWalkBlock({ onWalkMeThrough }: AstorWalkBlockProps) {
  const { t } = useI18n();
  return (
    <Box
      role="region"
      aria-label={t('consumer.signQueue.astorWalkTitle')}
      sx={{
        p: 2.5,
        borderRadius: 1,
        border: 1,
        borderColor: alpha(baseColors.teal[400], 0.4),
        bgcolor: alpha(baseColors.teal[400], 0.06),
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: 2,
      }}
    >
      <AstorAvatar size={24} />
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
          {t('consumer.signQueue.astorWalkTitle')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('consumer.signQueue.astorWalkBody')}
        </Typography>
      </Stack>
      <GlassButton
        size="small"
        variant="gradient"
        color="success"
        endIcon={<EastIcon />}
        onClick={onWalkMeThrough}
        sx={{ minHeight: 44, flexShrink: 0 }}
      >
        {t('consumer.signQueue.astorWalkCta')}
      </GlassButton>
    </Box>
  );
}

export default AstorWalkBlock;
